/**
 * `ws:` — API workspaces (Enter switches), `env:` — environments (names only,
 * never values, §4.2; Enter selects), `projects:` (Enter opens the project),
 * and teams (no prefix; Enter opens the team, as before).
 */
import { Briefcase, Variable, FolderKanban, Users, Settings2 } from 'lucide-react';
import { ORDER } from './order';
import { selectTop } from '../localSearch';
import { openAction } from '../actions';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useVariablesStore, setSelectedEnvironment } from '../../store/variablesStore';
import { useProjectScopeStore, setActiveProject } from '../../scope/projectScopeStore';
import { useTeamsStore } from '../../store/teamsStore';
import { setShowEnvironmentsModal } from '../../store/uiModalStore';
import { openProjectsProjectTab } from '../../store/tabsStore';
import { openPage } from './pages';
import { isApiClientEnabled } from '../../extensions/store';

function workspaceBadgeKey(ws) {
  if (ws?.storage === 'local') return 'palette.badge.local';
  if ((ws?.source || ws?.type) === 'team' || ws?.type === 'team') return 'palette.badge.team';
  return 'palette.badge.personal';
}

const recentFrom = (list) => async (ids, ctx) => {
  const all = list(ctx);
  return ids.map((id) => all.find((r) => r.id === id)).filter(Boolean);
};

export function workspaceResults(ctx) {
  return (useWorkspaceStore.getState().workspaces || [])
    .filter((ws) => ws?.id != null)
    .map((ws) => ({
      provider: 'workspaces',
      id: String(ws.id),
      title: ws.name || ctx.t('palette.fallback.workspace'),
      subtitle: ctx.t(workspaceBadgeKey(ws)),
      icon: Briefcase,
      searchFields: ws.description ? [{ text: ws.description, weight: 0.4 }] : [],
      payload: { workspace: ws, workspaceId: String(ws.id) },
    }));
}

export function createWorkspacesProvider() {
  return {
    id: 'workspaces',
    prefixes: ['workspaces', 'ws', 'workspace'],
    labelKey: 'palette.scope.workspaces',
    icon: Briefcase,
    order: ORDER.workspaces,
    kind: 'local-sync',
    // API workspaces belong to the API Client (#735): gone while it is switched off.
    available: (ctx) => ctx.platform === 'desktop' && isApiClientEnabled(),
    search: (req) => selectTop(workspaceResults(req.ctx), req),
    recent: recentFrom(workspaceResults),
    actions: () => [
      { ...openAction((r, ctx) => ctx.nav.onSwitchWorkspace?.(r.payload.workspace)), id: 'switch', titleKey: 'palette.action.switch' },
    ],
  };
}

export function environmentResults(ctx) {
  const { environments = [], selectedEnvironment } = useVariablesStore.getState();
  return environments
    .filter((env) => env?.id != null)
    .map((env) => ({
      provider: 'environments',
      id: String(env.id),
      title: env.name || ctx.t('palette.fallback.environment'),
      subtitle: selectedEnvironment && String(selectedEnvironment.id) === String(env.id) ? ctx.t('palette.badge.active') : undefined,
      icon: Variable,
      // Names only — a value never enters a result (§4.2).
      payload: { environmentId: String(env.id) },
    }));
}

export function createEnvironmentsProvider() {
  return {
    id: 'environments',
    prefixes: ['env', 'e', 'environment', 'environments'],
    labelKey: 'palette.scope.environments',
    icon: Variable,
    order: ORDER.environments,
    kind: 'local-sync',
    // API environments belong to the API Client: gone while it is switched off.
    available: (ctx) => ctx.platform === 'desktop' && isApiClientEnabled(),
    search: (req) => selectTop(environmentResults(req.ctx), req),
    recent: recentFrom(environmentResults),
    actions: () => [
      {
        ...openAction((r) => {
          const env = (useVariablesStore.getState().environments || []).find((e) => String(e.id) === r.payload.environmentId);
          if (env) setSelectedEnvironment(env);
        }),
        id: 'select',
        titleKey: 'palette.action.select',
      },
      { id: 'manage', titleKey: 'palette.action.manageEnvironments', icon: Settings2, run: () => setShowEnvironmentsModal(true) },
    ],
  };
}

export function projectResults(ctx) {
  const { known } = useProjectScopeStore.getState();
  return (known?.projects || [])
    .filter((p) => p?.id != null)
    .map((p) => ({
      provider: 'projects',
      id: String(p.id),
      title: p.name || ctx.t('palette.fallback.project'),
      subtitle: ctx.activeProjectId === String(p.id) ? ctx.t('palette.badge.active') : undefined,
      icon: FolderKanban,
      payload: { projectId: String(p.id), name: p.name },
    }));
}

export function createProjectsProvider() {
  return {
    id: 'projects',
    prefixes: ['projects', 'proj', 'project'],
    labelKey: 'palette.scope.projects',
    icon: FolderKanban,
    order: ORDER.projects,
    kind: 'local-sync',
    page: 'projects.panel',
    available: () => true,
    search: (req) => selectTop(projectResults(req.ctx), req),
    recent: recentFrom(projectResults),
    actions: () => [
      openAction((r) => {
        openPage('projects.panel');
        openProjectsProjectTab({ projectId: r.payload.projectId, label: r.payload.name });
      }),
      { id: 'switch', titleKey: 'palette.action.switchProject', icon: FolderKanban, run: (r) => setActiveProject(r.payload.projectId) },
    ],
  };
}

export function teamResults(ctx) {
  return (useTeamsStore.getState().teams || [])
    .filter((tm) => tm?.id != null)
    .map((tm) => ({
      provider: 'teams',
      id: String(tm.id),
      title: tm.name || ctx.t('palette.fallback.team'),
      subtitle: tm.description || undefined,
      icon: Users,
      payload: { team: tm },
    }));
}

export function createTeamsProvider() {
  return {
    id: 'teams',
    // No prefix: teams are not in the §2.2 table; they stay findable in global search as before.
    prefixes: [],
    labelKey: 'palette.scope.teams',
    icon: Users,
    order: ORDER.teams,
    kind: 'local-sync',
    available: (ctx) => !!ctx.currentUserId,
    search: (req) => selectTop(teamResults(req.ctx), req),
    recent: recentFrom(teamResults),
    actions: () => [openAction((r, ctx) => ctx.nav.onOpenTeam?.(r.payload.team))],
  };
}
